(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var instances = new WeakMap();
  var activeCurrency = null;
  var latestCart = null;

  /* -----------------------------------------------------------
   * Helpers
   * --------------------------------------------------------- */

  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getFocusableElements(root) {
    var nodes = root.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function parseJsonSafe(response) {
    return response.text().then(function (text) {
      if (!text) return {};
      try { return JSON.parse(text); } catch (e) { return {}; }
    });
  }

  /* -----------------------------------------------------------
   * CartDrawer
   * --------------------------------------------------------- */

  function CartDrawer(drawer) {
    this.drawer = drawer;
    this.controller = new AbortController();
    this.isOpen = false;
    this.previouslyFocused = null;
    var thresholdRaw = drawer.getAttribute('data-free-shipping-threshold');
    this.threshold = parseInt(thresholdRaw, 10) || 0;

    this.bindBaseEvents();
    this.bindCartItemDelegation();
    this.bindUpsellButtons();
    this.bindCheckoutButton();

    // Hidden by default via inline `display:none` — make sure CSS visibility
    // (driven by `is-open`) controls things from now on. We strip inline only
    // on first open so the drawer stays hidden until needed.
    this._inlineDisplayCleared = false;

    // Sync with current server-side cart on init so SSR Liquid stays in lockstep.
    this.refreshCart().catch(function () {
      // initial sync failures are non-fatal
    });
  }

  /* ---- UI: events / focus / open-close ---- */

  CartDrawer.prototype.bindBaseEvents = function () {
    var self = this;
    var signal = this.controller.signal;

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        self.close();
      }, { signal: signal });
    });

    this.drawer.addEventListener('keydown', function (e) {
      if (!self.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'Tab') {
        self.handleFocusTrap(e);
      }
    }, { signal: signal });
  };

  CartDrawer.prototype.handleFocusTrap = function (e) {
    var focusables = getFocusableElements(this.drawer);
    if (focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  CartDrawer.prototype.open = function () {
    if (this.isOpen) return;
    this.previouslyFocused = document.activeElement;
    if (!this._inlineDisplayCleared) {
      this.drawer.style.display = '';
      this._inlineDisplayCleared = true;
    }
    // Force reflow so opening transition runs.
    void this.drawer.offsetWidth;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;

    var self = this;
    setTimeout(function () {
      var focusables = getFocusableElements(self.drawer);
      if (focusables.length > 0) focusables[0].focus();
    }, 50);
  };

  CartDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.isOpen = false;
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      try { this.previouslyFocused.focus(); } catch (e) { /* noop */ }
    }
  };

  CartDrawer.prototype.toggle = function () {
    if (this.isOpen) this.close();
    else this.open();
  };

  /* ---- Cart AJAX API ---- */

  CartDrawer.prototype.getCart = function () {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin'
    }).then(parseJsonSafe).then(function (cart) {
      if (cart && cart.currency) activeCurrency = cart.currency;
      latestCart = cart;
      return cart;
    });
  };

  CartDrawer.prototype.refreshCart = function () {
    var self = this;
    return this.getCart().then(function (cart) {
      self.renderCart(cart);
      return cart;
    });
  };

  CartDrawer.prototype.addToCart = function (variantId, quantity, properties) {
    var self = this;
    var body = {
      id: variantId,
      quantity: quantity || 1,
      properties: properties || {}
    };
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        return parseJsonSafe(res).then(function (err) {
          throw err || new Error('Add to cart failed');
        });
      }
      return parseJsonSafe(res);
    }).then(function () {
      return self.getCart();
    }).then(function (cart) {
      self.renderCart(cart);
      return cart;
    });
  };

  CartDrawer.prototype.addMultipleToCart = function (items) {
    var self = this;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items || [] })
    }).then(function (res) {
      if (!res.ok) {
        return parseJsonSafe(res).then(function (err) {
          throw err || new Error('Bulk add to cart failed');
        });
      }
      return parseJsonSafe(res);
    }).then(function () {
      return self.getCart();
    }).then(function (cart) {
      self.renderCart(cart);
      return cart;
    });
  };

  CartDrawer.prototype.updateLineItemQuantity = function (lineKey, quantity) {
    var self = this;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }).then(function (res) {
      if (!res.ok) {
        return parseJsonSafe(res).then(function (err) {
          throw err || new Error('Update quantity failed');
        });
      }
      return parseJsonSafe(res);
    }).then(function (cart) {
      self.renderCart(cart);
      return cart;
    });
  };

  CartDrawer.prototype.removeFromCart = function (lineKey) {
    return this.updateLineItemQuantity(lineKey, 0);
  };

  CartDrawer.prototype.updateLineItemToSubscription = function (lineKey, sellingPlanId) {
    var self = this;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, selling_plan: sellingPlanId })
    }).then(function (res) {
      if (!res.ok) {
        return parseJsonSafe(res).then(function (err) {
          throw err || new Error('Subscription switch failed');
        });
      }
      return parseJsonSafe(res);
    }).then(function (cart) {
      self.renderCart(cart);
      return cart;
    });
  };

  /* ---- Render orchestrator ---- */

  CartDrawer.prototype.renderCart = function (cart) {
    if (!cart) return;
    if (cart.currency) activeCurrency = cart.currency;
    latestCart = cart;

    // IMPORTANT: never set innerHTML on [data-cart-additional-checkout] or any
    // ancestor that would tear down Shopify's <shopify-accelerated-checkout-cart>
    // web component. We only mutate scoped containers below.
    this.renderLineItems(cart);
    this.updateItemCount(cart);
    this.updateSubtotal(cart);
    this.updateSavings(cart);
    this.updateProgressBar(cart);
    this.toggleCartStates(cart);
    this.filterUpsells(cart);

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  };

  CartDrawer.prototype.renderLineItems = function (cart) {
    var containers = this.drawer.querySelectorAll('[data-cart-items]');
    if (!containers.length) return;

    var itemsHtml = (cart.items || []).map(function (item) {
      var itemTitle = item.product_title || item.title || '';
      var img = '<a href="' + escapeHtml(item.url) +
        '" class="oddit-cart-drawer__item-image-wrap" aria-label="' + escapeHtml(itemTitle) + '" tabindex="-1">';
      if (item.image) {
        img += '<img class="oddit-cart-drawer__item-image" src="' + escapeHtml(item.image) +
          '" alt="' + escapeHtml(itemTitle) +
          '" loading="lazy" width="80" height="80">';
      }
      img += '</a>';

      var variantTitle = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantTitle = '<p class="oddit-cart-drawer__item-variant">' +
          escapeHtml(item.variant_title) + '</p>';
      }

      var comparePrice = '';
      var badge = '';
      var hasCompare = item.original_price > item.final_price;
      if (hasCompare) {
        var savings = item.original_price - item.final_price;
        var percent = Math.round((savings * 100) / item.original_price);
        comparePrice = '<span class="oddit-cart-drawer__item-compare-price">' +
          formatMoney(item.original_price) + '</span>';
        badge = '<span class="oddit-cart-drawer__item-badge">SAVE ' + percent + '%</span>';
      }

      var key = escapeHtml(item.key);

      return '' +
        '<div class="oddit-cart-drawer__item" data-line-item data-line-item-key="' + key + '" data-line-key="' + key + '">' +
          img +
          '<div class="oddit-cart-drawer__item-info">' +
            '<h3 class="oddit-cart-drawer__item-title">' +
              '<a href="' + escapeHtml(item.url) + '" class="oddit-cart-drawer__item-title-link">' +
                escapeHtml(item.product_title || item.title || '') +
              '</a>' +
            '</h3>' +
            variantTitle +
            '<div class="oddit-cart-drawer__item-pricing">' +
              '<div class="oddit-cart-drawer__item-price-group">' +
                '<span class="oddit-cart-drawer__item-price' + (hasCompare ? ' oddit-cart-drawer__item-price--sale' : '') + '">' + formatMoney(item.final_price) + '</span>' +
                comparePrice +
              '</div>' +
              badge +
            '</div>' +
            '<div class="oddit-cart-drawer__item-actions">' +
              '<div class="oddit-cart-drawer__quantity">' +
                '<button type="button" class="oddit-cart-drawer__quantity-btn oddit-cart-drawer__quantity-btn--decrease" data-quantity-decrease data-quantity-action="decrease" data-line-item-key="' + key + '" data-line-key="' + key + '" aria-label="Decrease quantity">&ndash;</button>' +
                '<span class="oddit-cart-drawer__quantity-value" data-quantity-input data-line-item-key="' + key + '" data-line-key="' + key + '" aria-live="polite">' + (item.quantity || 0) + '</span>' +
                '<button type="button" class="oddit-cart-drawer__quantity-btn oddit-cart-drawer__quantity-btn--increase" data-quantity-increase data-quantity-action="increase" data-line-item-key="' + key + '" data-line-key="' + key + '" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="oddit-cart-drawer__remove" data-remove-item data-remove-line data-line-item-key="' + key + '" data-line-key="' + key + '">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    }).join('');

    // Preserve any returns banner sibling that lives inside [data-cart-items].
    containers.forEach(function (container) {
      var returnsBanner = container.querySelector('.oddit-cart-drawer__returns-banner');
      container.innerHTML = itemsHtml;
      if (returnsBanner) container.appendChild(returnsBanner);
    });
  };

  CartDrawer.prototype.updateItemCount = function (cart) {
    var count = cart.item_count || 0;
    var selectors = [
      '[data-cart-total-items]',
      '.oddit-cart-drawer__count',
      '.oddit-cart-drawer__subtotal-count'
    ].join(', ');
    this.drawer.querySelectorAll(selectors).forEach(function (el) {
      el.textContent = String(count);
    });
  };

  CartDrawer.prototype.updateSubtotal = function (cart) {
    var subtotal = (cart.items_subtotal_price != null) ? cart.items_subtotal_price : cart.total_price;
    this.drawer.querySelectorAll('[data-cart-subtotal]').forEach(function (el) {
      el.textContent = formatMoney(subtotal);
    });
  };

  CartDrawer.prototype.updateSavings = function (cart) {
    var savings = (cart.original_total_price || 0) - (cart.total_price || 0);
    this.drawer.querySelectorAll('[data-cart-savings]').forEach(function (el) {
      el.textContent = formatMoney(savings);
    });
  };

  CartDrawer.prototype.updateProgressBar = function (cart) {
    if (!this.threshold || this.threshold <= 0) return;
    var cartTotal = (cart.total_price || 0) / 100;
    var thresholdDollars = this.threshold / 100;
    var percentage = Math.min((cartTotal / thresholdDollars) * 100, 100);
    var remainingCents = Math.max((this.threshold - (cart.total_price || 0)), 0);

    this.drawer.querySelectorAll('.oddit-cart-drawer__shipping-fill').forEach(function (el) {
      el.style.width = percentage + '%';
    });

    this.drawer.querySelectorAll('.oddit-cart-drawer__shipping-text').forEach(function (el) {
      if (remainingCents <= 0) {
        el.innerHTML = "You've unlocked <strong>FREE shipping</strong>!";
      } else {
        el.innerHTML = "You're <strong>" + formatMoney(remainingCents) +
          "</strong> away from <strong>FREE shipping</strong>";
      }
    });
  };

  CartDrawer.prototype.toggleCartStates = function (cart) {
    var isEmpty = !cart.item_count || cart.item_count === 0;

    // [data-cart-state] containers if present
    this.drawer.querySelectorAll('[data-cart-state]').forEach(function (el) {
      var state = el.getAttribute('data-cart-state');
      var shouldShow =
        (state === 'empty' && isEmpty) ||
        (state === 'filled' && !isEmpty) ||
        (state === 'default' && !isEmpty);
      el.hidden = !shouldShow;
      el.style.display = shouldShow ? '' : 'none';
    });

    // Fallback to the explicit empty / filled markers used in this section
    var emptyEl = this.drawer.querySelector('[data-cart-empty]');
    var filledEl = this.drawer.querySelector('[data-cart-filled]');
    var footer = this.drawer.querySelector('.oddit-cart-drawer__footer, [data-cart-footer]');
    var reviews = this.drawer.querySelector('[data-cart-reviews]');

    if (emptyEl) {
      emptyEl.hidden = !isEmpty;
      emptyEl.style.display = isEmpty ? '' : 'none';
    }
    if (filledEl) {
      filledEl.hidden = isEmpty;
      filledEl.style.display = isEmpty ? 'none' : '';
    }
    if (footer) {
      footer.hidden = isEmpty;
      footer.style.display = isEmpty ? 'none' : '';
    }
    if (reviews) {
      reviews.hidden = isEmpty;
      reviews.style.display = isEmpty ? 'none' : '';
    }
  };

  CartDrawer.prototype.filterUpsells = function (cart) {
    var productIds = (cart.items || []).map(function (item) {
      return String(item.product_id);
    });
    var selector = [
      '[data-cross-sell-card][data-product-id]',
      '[data-upsell-card][data-product-id]',
      '.oddit-cart-drawer__rec-card[data-product-id]'
    ].join(', ');
    this.drawer.querySelectorAll(selector).forEach(function (card) {
      var pid = String(card.getAttribute('data-product-id'));
      card.style.display = productIds.indexOf(pid) !== -1 ? 'none' : '';
    });
  };

  /* ---- Delegated handlers ---- */

  CartDrawer.prototype.bindCartItemDelegation = function () {
    var self = this;
    var signal = this.controller.signal;

    this.drawer.addEventListener('click', function (e) {
      var dec = e.target.closest('[data-quantity-decrease]');
      var inc = e.target.closest('[data-quantity-increase]');
      var rem = e.target.closest('[data-remove-item], [data-remove-line]');
      var actionBtn = e.target.closest('[data-quantity-action]');

      function getKey(el) {
        return el.getAttribute('data-line-item-key') || el.getAttribute('data-line-key');
      }
      function getCurrentQty(key) {
        var qtyEl = self.drawer.querySelector(
          '[data-quantity-input][data-line-item-key="' + key + '"], [data-quantity-input][data-line-key="' + key + '"]'
        );
        return qtyEl ? (parseInt(qtyEl.textContent, 10) || 1) : 1;
      }
      function withDisabled(btn, promise) {
        btn.disabled = true;
        return promise.then(function (v) {
          btn.disabled = false;
          return v;
        }).catch(function (err) {
          btn.disabled = false;
          return Promise.reject(err);
        });
      }

      // Prefer explicit increase/decrease attributes over generic action
      if (dec) {
        e.preventDefault();
        var dKey = getKey(dec);
        var dNext = Math.max(0, getCurrentQty(dKey) - 1);
        withDisabled(dec, self.updateLineItemQuantity(dKey, dNext)).catch(function () {});
        return;
      }

      if (inc) {
        e.preventDefault();
        var iKey = getKey(inc);
        var iNext = Math.min(99, getCurrentQty(iKey) + 1);
        withDisabled(inc, self.updateLineItemQuantity(iKey, iNext)).catch(function () {});
        return;
      }

      if (actionBtn) {
        e.preventDefault();
        var aKey = getKey(actionBtn);
        var action = actionBtn.getAttribute('data-quantity-action');
        var current = getCurrentQty(aKey);
        var next = action === 'increase'
          ? Math.min(99, current + 1)
          : Math.max(0, current - 1);
        withDisabled(actionBtn, self.updateLineItemQuantity(aKey, next)).catch(function () {});
        return;
      }

      if (rem) {
        e.preventDefault();
        var rKey = getKey(rem);
        withDisabled(rem, self.removeFromCart(rKey)).catch(function () {});
        return;
      }
    }, { signal: signal });
  };

  CartDrawer.prototype.bindUpsellButtons = function () {
    var self = this;
    var signal = this.controller.signal;

    this.drawer.addEventListener('click', function (e) {
      // Cross-sell card add button
      var csBtn = e.target.closest(
        '.oddit-cart-drawer__rec-add[data-variant-id], ' +
        '.oddit-cart-drawer__cross-sell-add[data-variant-id], ' +
        '[data-add-to-cart][data-variant-id]'
      );
      if (csBtn) {
        e.preventDefault();
        var variantId = Number(csBtn.getAttribute('data-variant-id'));
        if (!variantId) return;
        csBtn.disabled = true;
        self.addToCart(variantId, 1).then(function () {
          csBtn.disabled = false;
        }).catch(function () {
          csBtn.disabled = false;
        });
        return;
      }

      // Bundle upsell CTA — single or multi-variant
      var upsellBtn = e.target.closest('.oddit-cart-drawer__upsell-cta[data-variant-id]');
      if (upsellBtn) {
        e.preventDefault();
        var raw = upsellBtn.getAttribute('data-variant-id');
        var ids = raw.split(',').map(function (s) { return Number(s.trim()); }).filter(Boolean);
        if (ids.length === 0) return;
        upsellBtn.disabled = true;
        var promise;
        if (ids.length === 1) {
          promise = self.addToCart(ids[0], 1);
        } else {
          promise = self.addMultipleToCart(ids.map(function (id) {
            return { id: id, quantity: 1 };
          }));
        }
        promise.then(function () {
          upsellBtn.disabled = false;
        }).catch(function () {
          upsellBtn.disabled = false;
        });
        return;
      }

      // Subscription switch CTA
      var subBtn = e.target.closest('.oddit-cart-drawer__switch-cta[data-selling-plan-id]');
      if (subBtn) {
        e.preventDefault();
        var sellingPlanId = subBtn.getAttribute('data-selling-plan-id');
        var productIdAttr = subBtn.getAttribute('data-product-id');
        if (!sellingPlanId || !latestCart) return;
        var targets = (latestCart.items || []).filter(function (item) {
          if (!productIdAttr) return true;
          return String(item.product_id) === String(productIdAttr);
        });
        if (!targets.length) return;
        subBtn.disabled = true;
        Promise.all(targets.map(function (item) {
          return self.updateLineItemToSubscription(item.key, sellingPlanId);
        })).then(function () {
          subBtn.disabled = false;
        }).catch(function () {
          subBtn.disabled = false;
        });
        return;
      }
    }, { signal: signal });
  };

  CartDrawer.prototype.bindCheckoutButton = function () {
    var signal = this.controller.signal;
    this.drawer.querySelectorAll(
      '.oddit-cart-drawer__checkout-cta, .oddit-cart-drawer__checkout-btn, [data-cart-checkout]'
    ).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        window.location.href = '/checkout';
      }, { signal: signal });
    });
  };

  CartDrawer.prototype.destroy = function () {
    this.controller.abort();
    if (this.isOpen) document.body.style.overflow = '';
  };

  /* -----------------------------------------------------------
   * Init + Section API
   * --------------------------------------------------------- */

  function initDrawer(drawer) {
    if (!drawer) return null;
    if (drawer.dataset.cartDrawerInitialized === 'true') {
      return instances.get(drawer) || null;
    }
    drawer.dataset.cartDrawerInitialized = 'true';
    var instance = new CartDrawer(drawer);
    instances.set(drawer, instance);
    return instance;
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initDrawer);
  }

  function getPrimaryInstance() {
    var first = document.querySelector(SECTION_SELECTOR);
    if (!first) return null;
    return instances.get(first) || initDrawer(first);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelector) return;
    var drawer = e.target.matches && e.target.matches(SECTION_SELECTOR)
      ? e.target
      : e.target.querySelector(SECTION_SELECTOR);
    if (drawer) initDrawer(drawer);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelector) return;
    var drawer = e.target.matches && e.target.matches(SECTION_SELECTOR)
      ? e.target
      : e.target.querySelector(SECTION_SELECTOR);
    if (!drawer) return;
    var inst = instances.get(drawer);
    if (inst) {
      inst.destroy();
      instances.delete(drawer);
    }
  });

  /* -----------------------------------------------------------
   * Public API — window.OdditCartDrawer
   * --------------------------------------------------------- */

  window.OdditCartDrawer = {
    open: function () {
      var inst = getPrimaryInstance();
      if (inst) inst.open();
    },
    close: function () {
      var inst = getPrimaryInstance();
      if (inst) inst.close();
    },
    toggle: function () {
      var inst = getPrimaryInstance();
      if (inst) inst.toggle();
    },
    addToCart: function (variantId, quantity, properties) {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.addToCart(variantId, quantity, properties).then(function (cart) {
        inst.open();
        return cart;
      });
    },
    addMultipleToCart: function (items) {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.addMultipleToCart(items).then(function (cart) {
        inst.open();
        return cart;
      });
    },
    removeFromCart: function (key) {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.removeFromCart(key);
    },
    updateLineItemQuantity: function (key, qty) {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.updateLineItemQuantity(key, qty);
    },
    updateLineItemToSubscription: function (key, planId) {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.updateLineItemToSubscription(key, planId);
    },
    getCart: function () {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.getCart();
    },
    refreshCart: function () {
      var inst = getPrimaryInstance();
      if (!inst) return Promise.reject(new Error('Cart drawer not initialized'));
      return inst.refreshCart();
    }
  };
})();